import { editorThemeColors } from "@/components/FullPageEditor/utils/editorThemeColors";
import Mountable from "@/components/styled/div/Mountable";
import { themeColors } from "@/utils/themeColors";
import { Editor } from "@knowt/editor/react";
import Skeleton from "@mui/material/Skeleton";
import React, { memo, useEffect, useRef } from "react";
import { Icon } from "../../ui/Icon";
import { Popover } from "../../ui/Popover";
import { Toolbar } from "../../ui/Toolbar";
import AIPill from "./components/AIPill";
import { AlignmentPicker } from "./components/AlignmentPicker";
import { ContentTypePicker } from "./components/ContentTypePicker";
import { EditLinkPopover } from "./components/EditLinkPopover";
import { useTextmenuAlignments } from "./hooks/useTextmenuAlignments";
import { useTextmenuCommands } from "./hooks/useTextmenuCommands";
import { useTextmenuContentTypes } from "./hooks/useTextmenuContentTypes";
import { useTextmenuStates } from "./hooks/useTextmenuStates";
import { FontFamilyPicker } from "./components/FontFamilyPicker";
import { FontSizePicker } from "./components/FontSizePicker";
import { ColorPicker } from "../../panels";
import { Surface } from "../../ui/Surface";
import { wait } from "@knowt/syncing/utils/genericUtils";
import { isEqual } from "lodash-es";

// NOTE: We memoize these buttons so each button is not rerendered every editor state changes
const MemoButton = memo(Toolbar.Button);
const MemoColorPicker = memo(ColorPicker);
const MemoFontFamilyPicker = memo(FontFamilyPicker);
const MemoFontSizePicker = memo(FontSizePicker);
const MemoContentTypePicker = memo(ContentTypePicker);
const MemoAlignmentPicker = memo(AlignmentPicker);
const MemoAIPill = memo(AIPill);

const TextToolbar = ({
    editor,
    isLoading,
    style,
    onExportPDF,
}: {
    editor: Editor;
    style?: React.CSSProperties;
    isLoading?: boolean;
    onExportPDF?: () => void;
}) => {
    const commands = useTextmenuCommands(editor);
    const states = useTextmenuStates(editor);
    const blockOptions = useTextmenuContentTypes(editor);
    const alignmentOptions = useTextmenuAlignments(editor);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = ref.current;
        if (!container || !editor?.isInitialized) return;

        const indicateToolbarIsScrollable = async () => {
            await wait(1500);
            container.scrollTo({ left: container.scrollWidth - container.clientWidth, behavior: "smooth" });
            await wait(1000);
            container.scrollTo({ left: 0, behavior: "smooth" });
        };

        indicateToolbarIsScrollable();
    }, [editor?.isInitialized]);

    if (isLoading) {
        return (
            <Skeleton
                variant="rectangular"
                sx={{
                    ...style,
                    borderRadius: "2.4rem",
                    bgcolor: themeColors.background,
                    margin: style?.margin,
                    height: style?.height || "4rem",
                    minHeight: style?.height || "4rem",
                    boxSizing: "content-box",
                    border: `1px solid ${editorThemeColors.neutral1}`,
                    width: /\d+/.test(String(style?.width)) ? style?.width : "min(60rem, 100%)",
                    alignSelf: "center",
                }}
            />
        );
    }

    return (
        <Toolbar.Wrapper
            ref={ref}
            className="scrollbar-thin"
            style={{
                minHeight: "4rem",
                zIndex: 90,
                alignSelf: "center",
                height: "fit-content",
                overflow: "auto hidden",
                borderRadius: "2.4rem",
                ...style,
            }}>
            <MemoAIPill editor={editor} />
            <MemoContentTypePicker options={blockOptions} />
            <MemoFontFamilyPicker onChange={commands.onSetFont} value={states.currentFont || ""} />
            <MemoFontSizePicker onChange={commands.onSetFontSize} value={states.currentSize || ""} />

            <Toolbar.Divider />

            <MemoAlignmentPicker alignmentOptions={alignmentOptions} />

            <MemoButton tooltip="Bold" tooltipShortcut={["Mod", "B"]} onClick={commands.onBold} active={states.isBold}>
                <Icon name="Bold" />
            </MemoButton>
            <MemoButton
                tooltip="Italic"
                tooltipShortcut={["Mod", "I"]}
                onClick={commands.onItalic}
                active={states.isItalic}>
                <Icon name="Italic" />
            </MemoButton>
            <MemoButton
                tooltip="Underline"
                tooltipShortcut={["Mod", "U"]}
                onClick={commands.onUnderline}
                active={states.isUnderline}>
                <Icon name="Underline" />
            </MemoButton>
            <MemoButton
                tooltip="Strikehrough"
                tooltipShortcut={["Mod", "Shift", "S"]}
                onClick={commands.onStrike}
                active={states.isStrike}>
                <Icon name="Strikethrough" />
            </MemoButton>

            <Popover
                content={
                    <Surface style={{ padding: "4px" }}>
                        <MemoColorPicker
                            color={states.currentHighlight}
                            onChange={commands.onChangeHighlight}
                            onClear={commands.onClearHighlight}
                        />
                    </Surface>
                }>
                {({ openPopup }) => (
                    <MemoButton active={!!states.currentHighlight} tooltip="Highlight text" onClick={openPopup}>
                        <Icon name="Highlighter" />
                    </MemoButton>
                )}
            </Popover>
            <Popover
                content={
                    <Surface style={{ padding: "4px" }}>
                        <MemoColorPicker
                            color={states.currentColor}
                            onChange={commands.onChangeColor}
                            onClear={commands.onClearColor}
                        />
                    </Surface>
                }>
                {({ openPopup }) => (
                    <MemoButton active={!!states.currentColor} tooltip="Text color" onClick={openPopup}>
                        <Icon name="Palette" />
                    </MemoButton>
                )}
            </Popover>

            <MemoButton tooltip="Math" onClick={commands.onMath}>
                <Icon name="Pi" />
            </MemoButton>
            <MemoButton tooltip="Code block" onClick={commands.onCodeBlock}>
                <Icon name="CodeXml" />
            </MemoButton>

            <MemoButton
                tooltip="Blockquote"
                onClick={() => editor.commands.setBlockquote()}
                active={editor?.isActive("blockquoteFigure")}>
                <Icon name="Quote" />
            </MemoButton>

            <EditLinkPopover onSetLink={commands.onLink} />

            <MemoButton
                tooltip="Add image"
                onClick={() => editor.chain().focus().setImageUpload().run()}
                active={false}>
                <Icon name="Image" />
            </MemoButton>

            <MemoButton
                tooltip="Horizontal divider"
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                active={false}>
                <Icon name="Minus" />
            </MemoButton>

            <MemoButton tooltip="Undo" onClick={() => editor.commands.undo()} active={false}>
                <Icon name="Undo" />
            </MemoButton>
            <MemoButton tooltip="Redo" onClick={() => editor.commands.redo()} active={false}>
                <Icon name="Redo" />
            </MemoButton>

            <Mountable mount={typeof onExportPDF === "function"}>
                <MemoButton tooltip="Export as PDF" onClick={onExportPDF} active={false}>
                    <Icon name="Printer" />
                </MemoButton>
            </Mountable>

            <Popover
                PopupProps={{ placement: "bottom" }}
                content={
                    <Toolbar.Wrapper>
                        <MemoButton
                            tooltip="Subscript"
                            tooltipShortcut={["Mod", ","]}
                            onClick={commands.onSubscript}
                            active={states.isSubscript}>
                            <Icon name="Subscript" />
                        </MemoButton>
                        <MemoButton
                            tooltip="Superscript"
                            tooltipShortcut={["Mod", "."]}
                            onClick={commands.onSuperscript}
                            active={states.isSuperscript}>
                            <Icon name="Superscript" />
                        </MemoButton>
                    </Toolbar.Wrapper>
                }>
                {({ openPopup }) => (
                    <MemoButton tooltip="More options" onClick={openPopup}>
                        <Icon name="EllipsisVertical" />
                    </MemoButton>
                )}
            </Popover>
        </Toolbar.Wrapper>
    );
};

export default memo(TextToolbar, (prevProps, nextProps) => isEqual(prevProps, nextProps));
